import { esc } from '../escape.js';
import { zonedParts, todayIn, addDays, dayLabel, shortTime } from '../scheduleTime.js';
import { swapButton } from '../homeCards.js';

// Home "My shift" card (#homeMyShiftCard). Fills the left-column slot for staff
// who don't get the Getting Started / Activity card: their own clock status,
// time worked today, breaks today and hours this week (Mon-Sun bars). Clocking
// itself happens only on the Time Clock (timeclock.html), so the card is
// read-only. Worked time ticks live from the cached shifts (no refetch).

const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const CLOCK_ICON = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>';
const COFFEE_ICON = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 8h1a4 4 0 1 1 0 8h-1"/><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z"/><line x1="6" y1="2" x2="6" y2="4"/><line x1="10" y1="2" x2="10" y2="4"/><line x1="14" y1="2" x2="14" y2="4"/></svg>';

// Monday 00:00 local of the current week.
export const weekStart = function (now = new Date()) {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
};

const overlap = (aStart, aEnd, bStart, bEnd) => Math.max(0, Math.min(aEnd, bEnd) - Math.max(aStart, bStart));

// Worked ms inside [from, to): shift time minus break time, open items run to `now`.
const workedBetween = function (shifts, from, to, now) {
  let ms = 0;
  for (const s of shifts) {
    const sIn = new Date(s.clockedInAt).getTime();
    const sOut = s.clockedOutAt ? new Date(s.clockedOutAt).getTime() : now;
    ms += overlap(sIn, sOut, from, to);
    for (const b of s.breaks) {
      const bIn = new Date(b.startedAt).getTime();
      const bOut = b.endedAt ? new Date(b.endedAt).getTime() : now;
      ms -= overlap(Math.max(bIn, sIn), Math.min(bOut, sOut), from, to);
    }
  }
  return Math.max(0, ms);
};

const fmtDur = function (ms, { hero = false } = {}) {
  const mins = Math.floor(ms / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (hero) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (h === 0) return `${m}m`;
  return m ? `${h}h ${m}m` : `${h}h`;
};

const fmtTime = iso => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

class MyShiftView {
  _card = document.getElementById('homeMyShiftCard');
  _data = null;
  _timer = null;

  // data: { name, roleName, timeTracking, shifts }
  render(data) {
    if (!this._card) return;
    this._data = data;
    this._paint();
    this._card.classList.remove('hidden');
    clearInterval(this._timer);
    // Minute-resolution display, so a 20s tick is plenty and cheap.
    if (data.timeTracking) this._timer = setInterval(() => { if (!document.hidden) this._paint(); }, 20000);
  }

  hide() {
    clearInterval(this._timer);
    this._timer = null;
    this._data = null;
    this._card?.classList.add('hidden');
  }

  _paint() {
    const d = this._data;
    if (!d) return;
    const initials = (d.name || '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?';
    const head = (pill) => `
      <div class="ms-head">
        <div class="ms-who">
          <span class="ms-avatar" aria-hidden="true">${esc(initials)}</span>
          <div class="ms-who-text">
            <p class="ms-eyebrow">My shift</p>
            <h3 class="ms-name">${esc(d.name || 'Staff member')}</h3>
            ${d.roleName ? `<span class="ms-role">${esc(d.roleName)}</span>` : ''}
          </div>
        </div>
        <div class="ms-head-end">${pill}${swapButton('main')}</div>
      </div>`;

    if (!d.timeTracking) {
      this._card.innerHTML = `
        ${head('<span class="ms-pill"><span class="ms-pill-dot"></span>Time tracking off</span>')}
        <div class="ms-off">
          <span class="ms-off-icon" aria-hidden="true">${CLOCK_ICON}</span>
          <p class="ms-off-title">Time tracking is off for your role</p>
          <p class="ms-off-text">Your role doesn't use the Time Clock, so there are no shifts to show here. If you should be clocking in, ask your manager to turn on time tracking for your role.</p>
        </div>`;
      return;
    }

    const now = Date.now();
    const shifts = d.shifts ?? [];
    const open = shifts.find(s => !s.clockedOutAt);
    const openBreak = open?.breaks.find(b => !b.endedAt);
    const today = new Date();
    const dayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    const dayEnd = dayStart + 86400000;
    const wkStart = weekStart(today);
    const todayIdx = (today.getDay() + 6) % 7;

    const workedToday = workedBetween(shifts, dayStart, dayEnd, now);
    const days = DAY_LABELS.map((_, i) => {
      const from = new Date(wkStart.getFullYear(), wkStart.getMonth(), wkStart.getDate() + i).getTime();
      const to = new Date(wkStart.getFullYear(), wkStart.getMonth(), wkStart.getDate() + i + 1).getTime();
      return workedBetween(shifts, from, to, now);
    });
    const weekTotal = days.reduce((a, b) => a + b, 0);

    let breakCount = 0, breakMs = 0;
    for (const s of shifts) for (const b of s.breaks) {
      const bIn = new Date(b.startedAt).getTime();
      if (bIn < dayStart || bIn >= dayEnd) continue;
      breakCount++;
      breakMs += (b.endedAt ? new Date(b.endedAt).getTime() : now) - bIn;
    }

    let state, pill, label;
    if (openBreak) {
      state = 'break';
      pill = `<span class="ms-pill ms-pill--break"><span class="ms-pill-dot"></span>On break &middot; ${fmtDur(now - new Date(openBreak.startedAt).getTime())}</span>`;
      label = `worked today &middot; break started ${fmtTime(openBreak.startedAt)}`;
    } else if (open) {
      state = 'on';
      pill = '<span class="ms-pill ms-pill--on"><span class="ms-pill-dot"></span>On shift</span>';
      label = `worked today &middot; clocked in at ${fmtTime(open.clockedInAt)}`;
    } else {
      state = 'off';
      pill = '<span class="ms-pill"><span class="ms-pill-dot"></span>Not clocked in</span>';
      label = workedToday > 0 ? 'worked today &middot; clocked out' : 'not clocked in yet today';
    }

    // Bars scale to the busiest day this week (min 8h so a short day isn't a full bar).
    const scale = Math.max(8 * 3600000, ...days);
    const bars = days.map((ms, i) => `
      <div class="ms-day${i === todayIdx ? ' ms-day--today' : ''}" title="${fmtDur(ms)}">
        <div class="ms-bar-track"><div class="ms-bar" style="height:${Math.max(6, Math.round((ms / scale) * 100))}%"></div></div>
        <span>${DAY_LABELS[i]}</span>
      </div>`).join('');

    this._card.innerHTML = `
      ${head(pill)}
      <div class="ms-hero">
        <span class="ms-hero-icon ms-hero-icon--${state}" aria-hidden="true">${state === 'break' ? COFFEE_ICON : CLOCK_ICON}</span>
        <div>
          <div class="ms-time">${fmtDur(workedToday, { hero: true })}</div>
          <div class="ms-time-label">${label}</div>
        </div>
      </div>
      <div class="ms-stats">
        <div class="ms-stat">
          <div class="ms-stat-label">Breaks today</div>
          <div class="ms-stat-value">${breakCount ? `${breakCount} &middot; ${fmtDur(breakMs)}` : 'None'}</div>
        </div>
        <div class="ms-stat">
          <div class="ms-stat-label">This week</div>
          <div class="ms-stat-value">${fmtDur(weekTotal)}</div>
        </div>
      </div>
      <div class="ms-week" aria-label="Hours worked each day this week">${bars}</div>
      ${this._swapAskMarkup(d)}
      ${this._nextShiftMarkup(d) || `<div class="ms-foot">${CLOCK_ICON}Clock in, take breaks and clock out on the Time Clock.</div>`}`;
  }

  // A teammate asked them to cover or trade a shift (2026-10-17). Answered on
  // the Time Clock's My Schedule page; in-app only, no email.
  _swapAskMarkup(d) {
    const n = d.swapAsks ?? 0;
    if (!n) return '';
    return `<div class="ms-ask"><b>${n === 1 ? 'A teammate asked you to take a shift' : `${n} teammates asked you to take shifts`}</b><span>Answer on the Time Clock, under My schedule.</span></div>`;
  }

  // Next PUBLISHED scheduled shift (2026-10-15), in the business timezone.
  _nextShiftMarkup(d) {
    const s = d.nextShift;
    if (!s || !d.tz) return '';
    const now = Date.now();
    if (new Date(s.endsAt).getTime() <= now) return '';
    const day = zonedParts(new Date(s.startsAt), d.tz).dateStr;
    const today = todayIn(d.tz);
    const range = `${shortTime(new Date(s.startsAt), d.tz)} – ${shortTime(new Date(s.endsAt), d.tz)}`;
    const nowOn = new Date(s.startsAt).getTime() <= now;
    const when = nowOn ? `Now, until ${shortTime(new Date(s.endsAt), d.tz)}`
      : day === today ? `Today ${range}`
      : day === addDays(today, 1) ? `Tomorrow ${range}`
      : `${dayLabel(day)} · ${range}`;
    return `
      <div class="ms-next" style="--sc:${esc(s.stationColor || '#0FA267')}">
        <span class="ms-next-label">${nowOn ? 'Scheduled shift' : 'Next shift'}</span>
        <span class="ms-next-when">${esc(when)}</span>
        ${s.stationName ? `<span class="ms-next-station">${esc(s.stationName)}</span>` : ''}
      </div>`;
  }
}

export default new MyShiftView();
